import React from "react";
import { Card, Typography, Chip, Stack, Collapse, Button } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box } from "@mui/system";
import {
  typography,
  spacing,
  colors,
  shadows,
} from "../../../../../../../../../assets/styles/tokens";
import BrandsSearchItemsMoreMetrics from "./BrandsSearchItemsMoreMetrics";
import BrandsSearchItemsCardLastRun from "./BrandsSearchItemsCardLastRun";

type StatusColor = "success" | "warning" | "error";

interface BrandsSearchItemsCardProps {
  title: string;
  brandId: string;
  value: string;
  clickOut: string;
  roi: string;
  roiColor: StatusColor;
  ftd: string;
  ftdPercentage: string;
  ftdColor: StatusColor;
  qftd: string;
  qftdPercentage: string;
  qftdColor: StatusColor;
  cpaIncome: string;
  rsIncome: string;
  totalIncome: string;
  pv: string;
  epc: string;
  epcCpc: string;

  runDate: string;
  runRange: string;
  runBy: string;
  status: string;
}

const buildShadow = (shadowToken: any) =>
  shadowToken.$value
    .map(
      (s: any) => `${s.offsetX} ${s.offsetY} ${s.blur} ${s.spread} ${s.color}`,
    )
    .join(", ");

export function BrandsSearchItemsCard({
  title,
  brandId,
  value,
  clickOut,
  roi,
  roiColor,
  ftd,
  ftdPercentage,
  ftdColor,
  qftd,
  qftdColor,
  qftdPercentage,
  cpaIncome,
  rsIncome,
  totalIncome,
  pv,
  epc,
  epcCpc,
  runDate,
  runRange,
  runBy,
  status,
}: BrandsSearchItemsCardProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Card
        sx={{
          p: 1,
          borderRadius: spacing.Radius.lg.$value,
          boxShadow: buildShadow(shadows.Shadows.sm),
          border: "1px solid" + colors.GrayLightMode[200].$value,
          mb: spacing.Spacing.xs.$value + "!important",
          "&:not(:first-of-type)": {
            mt: spacing.Spacing.lg.$value,
          },
        }}
      >
        {/* Top Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="body1"
            sx={{
              color: colors.GrayLightMode[700].$value,
              fontWeight: typography.Font_weight.Medium.$value,
              fontSize: typography.Size.Text.xs.$value,
              m: 0,
            }}
          >
            {title} ({brandId})
          </Typography>

          <BrandsSearchItemsCardLastRun
            title={title}
            brandId={brandId}
            runDate={runDate}
            runRange={runRange}
            runBy={runBy}
            status={status}
          />
        </Stack>

        <Stack sx={{ mb: spacing.Spacing.xs.$value }}>
          <Typography
            variant="body1"
            sx={{ fontSize: typography.Size.Text.sm.$value }}
          >
            <Box
              component="span"
              sx={{
                fontSize: typography.Size.Text.xl.$value,
                fontWeight: typography.Font_weight.Medium.$value,
                mr: spacing.Spacing.xs.$value,
              }}
            >
              {value}
            </Box>
            Registrations of
            <Box
              component="span"
              sx={{ fontWeight: typography.Font_weight.Medium.$value }}
            >
              {" "}
              {clickOut}{" "}
            </Box>
            Click out
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display={"flex"} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
                width: spacing.Spacing["5xl"].$value,
              }}
            >
              FTD
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
                fontWeight: typography.Font_weight.Bold.$value,
                mx: 1,
              }}
            >
              {ftd}
            </Typography>
            <Chip
              label={ftdPercentage}
              size="small"
              sx={{
                borderRadius: spacing.Radius.xs.$value,
                bgcolor:
                  ftdColor === "success"
                    ? colors.Success[50].$value
                    : ftdColor === "warning"
                      ? colors.Warning[50].$value
                      : colors.Error[50].$value,

                color:
                  ftdColor === "success"
                    ? colors.Success[600].$value
                    : ftdColor === "warning"
                      ? colors.Warning[600].$value
                      : colors.Error[600].$value,

                fontWeight: typography.Font_weight.Semibold.$value,
              }}
            />
          </Box>
          <Box display={"flex"} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
                mr: 1,
              }}
            >
              ROI
            </Typography>
            <Chip
              label={roi}
              size="small"
              sx={{
                borderRadius: spacing.Radius.xs.$value,
                bgcolor:
                  roiColor === "success"
                    ? colors.Success[50].$value
                    : roiColor === "warning"
                      ? colors.Warning[50].$value
                      : colors.Error[50].$value,

                color:
                  roiColor === "success"
                    ? colors.Success[600].$value
                    : roiColor === "warning"
                      ? colors.Warning[600].$value
                      : colors.Error[600].$value,

                fontWeight: typography.Font_weight.Semibold.$value,
              }}
            />
          </Box>
        </Stack>

        {/* Predicted Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mt={spacing.Spacing.sm.$value}
        >
          <Box display={"flex"} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
                width: spacing.Spacing["5xl"].$value,
              }}
            >
              QFTD
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
                fontWeight: typography.Font_weight.Bold.$value,
                mx: 1,
              }}
            >
              {qftd}
            </Typography>
            <Chip
              label={qftdPercentage}
              size="small"
              sx={{
                borderRadius: spacing.Radius.xs.$value,
                bgcolor:
                  qftdColor === "success"
                    ? colors.Success[50].$value
                    : qftdColor === "warning"
                      ? colors.Warning[50].$value
                      : colors.Error[50].$value,

                color:
                  qftdColor === "success"
                    ? colors.Success[600].$value
                    : qftdColor === "warning"
                      ? colors.Warning[600].$value
                      : colors.Error[600].$value,

                fontWeight: typography.Font_weight.Semibold.$value,
              }}
            />
          </Box>

          <Button
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              padding: "0",
              fontSize: typography.Size.Text.xs.$value,
              color: colors.GrayLightMode[900].$value,
              fontWeight: typography.Font_weight.Regular.$value,
              textTransform: "capitalize",
              letterSpacing: "0px",
            }}
            data-tracker="654"
          >
            {open ? "Less Metrics" : "More Metrics"}
            <ExpandMoreIcon
              sx={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "0.2s",
              }}
            />
          </Button>
        </Stack>

        {/* Expandable Info */}
        <Collapse in={open}>
          <BrandsSearchItemsMoreMetrics
            cpaIncome={cpaIncome}
            rsIncome={rsIncome}
            totalIncome={totalIncome}
            pv={pv}
            epc={epc}
            epcCpc={epcCpc}
          />
        </Collapse>
      </Card>
    </>
  );
}

export default BrandsSearchItemsCard;
